/**
 * BrainKBEntriesTab
 * =================
 * KB entries table with category filters, search, CRUD modals, and history button.
 * Extracted from KnowledgeBasePage logic for embedding in ZamanatBrainPage.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../hooks';
import { Alert, Button } from '../ui';
import { getCategories, getEntries, createEntry, updateEntry, deleteEntry } from '../../api/knowledgeBase';
import KBEntryHistoryModal from './KBEntryHistoryModal';

/* ── helpers: convert between {key,value}[] ↔ plain object ── */
const objectToFields = (data) => {
  if (!data || typeof data !== 'object') return [{ key: '', value: '' }];
  const entries = Object.entries(data);
  if (entries.length === 0) return [{ key: '', value: '' }];
  return entries.map(([key, value]) => ({
    key,
    value: typeof value === 'string' ? value : JSON.stringify(value),
  }));
};

const fieldsToObject = (fields) => {
  const obj = {};
  fields.forEach(({ key, value }) => {
    if (!key.trim()) return;
    try {
      obj[key.trim()] = JSON.parse(value);
    } catch {
      obj[key.trim()] = value;
    }
  });
  return obj;
};

const BrainKBEntriesTab = () => {
  const { showToast } = useToast();
  const [categories, setCategories] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({ category: '', name: '', dataFields: [{ key: '', value: '' }] });
  const [historyEntry, setHistoryEntry] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});

  const fetchCategories = useCallback(async () => {
    try {
      const res = await getCategories();
      setCategories(res.data?.categories || []);
    } catch { showToast('Failed to load categories', 'error'); }
  }, [showToast]);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await getEntries(null);
      setEntries(res.data?.entries || []);
    } catch (err) { setFetchError(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const filteredEntries = searchQuery
    ? entries.filter(e =>
        e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.category.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : entries;

  // Group entries by category
  const groupedEntries = filteredEntries.reduce((acc, entry) => {
    if (!acc[entry.category]) acc[entry.category] = [];
    acc[entry.category].push(entry);
    return acc;
  }, {});

  const sortedCategoryKeys = Object.keys(groupedEntries).sort();

  // When searching, auto-expand categories that have matching entries
  useEffect(() => {
    if (searchQuery) {
      const matchingCategories = {};
      sortedCategoryKeys.forEach(cat => { matchingCategories[cat] = true; });
      setExpandedCategories(matchingCategories);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const handleCreate = async () => {
    try {
      const parsedData = fieldsToObject(formData.dataFields);
      await createEntry({ category: formData.category, name: formData.name, data: parsedData });
      showToast('Entry created', 'success');
      setShowCreateModal(false);
      setFormData({ category: '', name: '', dataFields: [{ key: '', value: '' }] });
      fetchEntries();
      fetchCategories();
    } catch (err) { showToast(err.response?.data?.detail || 'Failed to create entry', 'error'); }
  };

  const handleUpdate = async () => {
    if (!editingEntry) return;
    try {
      const parsedData = fieldsToObject(formData.dataFields);
      await updateEntry(editingEntry.id, { name: formData.name, data: parsedData });
      showToast('Entry updated', 'success');
      setEditingEntry(null);
      fetchEntries();
    } catch (err) { showToast(err.response?.data?.detail || 'Failed to update entry', 'error'); }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Deactivate this entry?')) return;
    try {
      await deleteEntry(entryId);
      showToast('Entry deactivated', 'success');
      fetchEntries();
      fetchCategories();
    } catch (err) { showToast(err.response?.data?.detail || 'Failed to delete entry', 'error'); }
  };

  const startEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      category: entry.category,
      name: entry.name,
      dataFields: objectToFields(entry.data),
    });
  };

  const updateField = (index, field, value) => {
    const updated = [...formData.dataFields];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, dataFields: updated });
  };

  const addField = () => {
    setFormData({ ...formData, dataFields: [...formData.dataFields, { key: '', value: '' }] });
  };

  const removeField = (index) => {
    const updated = formData.dataFields.filter((_, i) => i !== index);
    setFormData({ ...formData, dataFields: updated.length ? updated : [{ key: '', value: '' }] });
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between mb-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          <div className="relative w-full sm:w-auto">
            <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search entries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-full sm:w-64 focus:ring-0 focus:border-primary transition-all"
            />
          </div>
          <span className="text-sm text-gray-500">
            {sortedCategoryKeys.length} categories, {filteredEntries.length} entities
          </span>
        </div>
        <button
          onClick={() => { setShowCreateModal(true); setFormData({ category: '', name: '', dataFields: [{ key: '', value: '' }] }); }}
          className="w-full sm:w-auto px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors shadow-md"
        >
          Add Entry
        </button>
      </div>

      {/* Category Rows */}
      {fetchError ? (
        <div className="py-4">
          <Alert
            variant={fetchError.code === 'NETWORK_ERROR' ? 'info' : 'error'}
            title={fetchError.code === 'NETWORK_ERROR' ? 'Connection Issue' : 'Failed to Load Entries'}
          >
            {fetchError.message || 'An unexpected error occurred.'}
          </Alert>
          <div className="mt-4 text-center">
            <Button onClick={fetchEntries}>Retry</Button>
          </div>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : sortedCategoryKeys.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <p className="text-gray-500">No entries found.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sortedCategoryKeys.map(category => {
            const categoryEntries = groupedEntries[category];
            const isExpanded = !!expandedCategories[category];

            return (
              <div key={category} className="bg-white rounded-xl shadow-soft border border-gray-200 overflow-hidden">
                {/* Category Header Row */}
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <svg
                      className={`w-4 h-4 shrink-0 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="inline-flex items-center text-left px-2.5 py-1 rounded-md text-sm font-semibold bg-primary-50 text-primary-700">
                      {category.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <span className="shrink-0 ml-2 whitespace-nowrap text-xs font-medium text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">
                    {categoryEntries.length} {categoryEntries.length === 1 ? 'entity' : 'entities'}
                  </span>
                </button>

                {/* Expanded Entity List */}
                {isExpanded && (
                  <div className="border-t border-gray-100 overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-100">
                      <thead className="bg-gray-50/60">
                        <tr>
                          <th className="px-3 sm:px-5 py-2 text-left text-xs font-semibold text-gray-400 uppercase pl-6 sm:pl-12">Name</th>
                          <th className="px-3 sm:px-5 py-2 text-left text-xs font-semibold text-gray-400 uppercase">Data Fields</th>
                          <th className="px-3 sm:px-5 py-2 text-right text-xs font-semibold text-gray-400 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {categoryEntries.map(entry => (
                          <tr key={entry.id} className="hover:bg-gray-50/80 transition-colors">
                            <td className="px-3 sm:px-5 py-2.5 text-sm font-medium text-gray-900 pl-6 sm:pl-12">{entry.name}</td>
                            <td className="px-3 sm:px-5 py-2.5 text-sm text-gray-500 whitespace-nowrap">
                              {Object.keys(entry.data || {}).length} fields
                            </td>
                            <td className="px-3 sm:px-5 py-2.5 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={(e) => { e.stopPropagation(); startEdit(entry); }}
                                  title="Edit"
                                  className="p-1.5 rounded-lg text-primary hover:text-primary-600 hover:bg-primary-50 transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setHistoryEntry(entry); }}
                                  title="Version history"
                                  className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDelete(entry.id); }}
                                  title="Deactivate"
                                  className="p-1.5 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingEntry) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="px-6 pt-6 pb-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-900">
                {editingEntry ? 'Edit Entry' : 'Create Entry'}
              </h2>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-4">
                {!editingEntry && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <input
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full rounded-lg border-gray-300 text-sm px-4 py-2.5 border focus:ring-0 focus:border-primary"
                      placeholder="e.g. asset_classes"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full rounded-lg border-gray-300 text-sm px-4 py-2.5 border focus:ring-0 focus:border-primary"
                    placeholder="Entry name"
                  />
                </div>

                {/* Dynamic Key-Value Fields */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label className="flex-1 min-w-0 text-sm font-medium text-gray-700">Data Fields</label>
                    <label className="flex-[2] min-w-0 text-sm font-medium text-gray-700">Value</label>
                    <div className="w-8 flex-shrink-0"></div>
                  </div>
                  <div className="space-y-2">
                    {formData.dataFields.map((field, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <input
                            value={field.key}
                            onChange={(e) => updateField(index, 'key', e.target.value)}
                            className="w-full rounded-lg border-gray-300 text-sm px-3 py-2 border focus:ring-0 focus:border-primary"
                            placeholder="Field name"
                          />
                        </div>
                        <div className="flex-[2] min-w-0">
                          {field.value.length > 80 || field.value.includes('\n') ? (
                            <textarea
                              value={field.value}
                              onChange={(e) => updateField(index, 'value', e.target.value)}
                              rows={3}
                              className="w-full rounded-lg border-gray-300 text-sm px-3 py-2 border focus:ring-0 focus:border-primary"
                              placeholder="Value"
                            />
                          ) : (
                            <input
                              value={field.value}
                              onChange={(e) => updateField(index, 'value', e.target.value)}
                              className="w-full rounded-lg border-gray-300 text-sm px-3 py-2 border focus:ring-0 focus:border-primary"
                              placeholder="Value"
                            />
                          )}
                        </div>
                        <button
                          onClick={() => removeField(index)}
                          className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                          title="Remove field"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={addField}
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Field
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => { setShowCreateModal(false); setEditingEntry(null); }}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={editingEntry ? handleUpdate : handleCreate}
                className="px-4 py-2 text-sm text-white bg-primary rounded-lg hover:bg-primary-600 shadow-md"
              >
                {editingEntry ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {historyEntry && (
        <KBEntryHistoryModal
          entryId={historyEntry.id}
          entryName={`${historyEntry.category}/${historyEntry.name}`}
          onClose={() => setHistoryEntry(null)}
          onReverted={() => fetchEntries()}
        />
      )}
    </div>
  );
};

export default BrainKBEntriesTab;
